/** @jsxImportSource davaux/client */
import { createSignal } from 'davaux/client'

export default function PinInput({
  length = 4,
  name,
  type = 'alphanumeric',
  mask = false,
  placeholder = '○',
  disabled = false,
  className = '',
}: {
  length?: number
  name?: string
  type?: 'alphanumeric' | 'number'
  mask?: boolean
  placeholder?: string
  disabled?: boolean
  className?: string
}) {
  const [values, setValues] = createSignal<string[]>(Array(length).fill(''))

  function getInputs(root: HTMLElement) {
    return Array.from(root.querySelectorAll<HTMLInputElement>('[data-pin-index]'))
  }

  function handleInput(i: number, e: Event) {
    const target = e.target as HTMLInputElement
    const char = target.value.slice(-1)
    const next = [...values()]
    next[i] = char
    setValues(next)
    if (char && i < length - 1) {
      const root = target.closest('.dv-pin-input') as HTMLElement
      if (root) {
        const inputs = getInputs(root)
        inputs[i + 1]?.focus()
      }
    }
  }

  function handleKeyDown(i: number, e: KeyboardEvent) {
    if (e.key === 'Backspace' && !values()[i] && i > 0) {
      const root = (e.target as HTMLElement).closest('.dv-pin-input') as HTMLElement
      if (root) {
        const inputs = getInputs(root)
        const next = [...values()]
        next[i - 1] = ''
        setValues(next)
        inputs[i - 1].value = ''
        inputs[i - 1]?.focus()
      }
    }
  }

  function handlePaste(e: ClipboardEvent) {
    e.preventDefault()
    const text = e.clipboardData?.getData('text') ?? ''
    const chars = text.slice(0, length).split('')
    const next = Array(length).fill('')
    chars.forEach((c, idx) => {
      next[idx] = c
    })
    setValues(next)
    const root = (e.target as HTMLElement).closest('.dv-pin-input') as HTMLElement
    if (root) {
      const inputs = getInputs(root)
      inputs.forEach((inp, idx) => {
        inp.value = next[idx] ?? ''
      })
      const lastIdx = Math.min(chars.length, length - 1)
      inputs[lastIdx]?.focus()
    }
  }

  const inputType = mask ? 'password' : type === 'number' ? 'tel' : 'text'

  return (
    <div
      class={`dv-pin-input${className ? ` ${className}` : ''}`}
      data-disabled={disabled || undefined}
    >
      {Array.from({ length }, (_, i) => (
        <input
          class='dv-pin-input-field'
          type={inputType}
          maxLength={1}
          data-pin-index={i}
          value={values()[i]}
          placeholder={placeholder}
          disabled={disabled}
          aria-label={`PIN digit ${i + 1}`}
          inputMode={type === 'number' ? 'numeric' : undefined}
          onInput={(e: Event) => handleInput(i, e)}
          onKeyDown={(e: KeyboardEvent) => handleKeyDown(i, e)}
          onPaste={handlePaste}
        />
      ))}
      {name && <input type='hidden' name={name} value={() => values().join('')} />}
    </div>
  )
}
